import notify from 'components/atoms/Notify'
import { Formik, Form } from 'formik'
import apiPost from 'lib/network/apiPost'
import { useState } from 'react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import DatePicker, { registerLocale } from 'react-datepicker'
import { arSA, enUS } from 'date-fns/locale'
import moment from 'moment'
import _ from 'lodash'
import 'react-datepicker/dist/react-datepicker.css'

registerLocale('ar', arSA)
registerLocale('en', enUS)

const CarBooking = ({ carId, onSuccess }) => {
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState(false)

  const isArabic = i18n.language === 'ar'
  const datePickerLocale = isArabic ? 'ar' : 'en'

  const formatDateForBackend = date => {
    if (!date) return null
    // Always format using English numerals by using native Date methods
    const d = new Date(date)
    const year = d.getFullYear()
    const month = _.padStart(String(d.getMonth() + 1), 2, '0')
    const day = _.padStart(String(d.getDate()), 2, '0')
    return `${year}-${month}-${day}`
  }

  const BookingSchema = Yup.object().shape({
    bookingFrom: Yup.date().nullable().required(t('carBooking.validation.bookingFromRequired')),
    bookingTo: Yup.date().nullable().required(t('carBooking.validation.bookingToRequired')).min(Yup.ref('bookingFrom'), t('carBooking.validation.bookingToAfterFrom')),
  })

  const handleSubmit = async values => {
    setLoading(true)
    try {
      const response = await apiPost(`car/${carId}/book`, {
        rentalStartDate: formatDateForBackend(values.bookingFrom),
        rentalEndDate: formatDateForBackend(values.bookingTo),
      })

      if (response.success) {
        onSuccess()
      } else {
        // Check if the error is about car already being booked
        const errorMessage = response.message || ''
        if (_.includes(_.toLower(errorMessage), 'already booked')) {
          notify.error(t('carBooking.messages.carAlreadyBooked'))
        } else {
          notify.error(errorMessage || t('carBooking.messages.bookingFailed'))
        }
      }
    } catch (error) {
      const errorMessage = error.message || ''
      if (_.includes(_.toLower(errorMessage), 'already booked')) {
        notify.error(t('carBooking.messages.carAlreadyBooked'))
      } else {
        notify.error(errorMessage || t('carBooking.messages.bookingFailed'))
      }
    }
    setLoading(false)
  }

  return (
    <div className="p-6">
      <Formik initialValues={{ bookingFrom: null, bookingTo: null }} validationSchema={BookingSchema} onSubmit={handleSubmit}>
        {({ values, errors, touched, setFieldValue }) => (
          <Form className="space-y-4">
            <div>
              <label htmlFor="bookingFrom" className="block text-sm font-medium text-gray-700 mb-1">
                {t('carBooking.form.bookingFrom')} *
              </label>
              <div className="max-w-xs" dir={isArabic ? 'rtl' : 'ltr'}>
                <DatePicker
                  selected={values.bookingFrom}
                  onChange={date => setFieldValue('bookingFrom', date)}
                  dateFormat="dd/MM/yyyy"
                  locale={datePickerLocale}
                  placeholderText={isArabic ? 'اختر التاريخ' : 'dd/mm/yyyy'}
                  className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                  popperPlacement="bottom-start"
                />
              </div>
              {errors.bookingFrom && touched.bookingFrom && <div className="mt-1 text-sm text-red-500">{errors.bookingFrom}</div>}
            </div>

            <div>
              <label htmlFor="bookingTo" className="block text-sm font-medium text-gray-700 mb-1">
                {t('carBooking.form.bookingTo')} *
              </label>
              <div className="max-w-xs" dir={isArabic ? 'rtl' : 'ltr'}>
                <DatePicker
                  selected={values.bookingTo}
                  onChange={date => setFieldValue('bookingTo', date)}
                  dateFormat="dd/MM/yyyy"
                  locale={datePickerLocale}
                  placeholderText={isArabic ? 'اختر التاريخ' : 'dd/mm/yyyy'}
                  className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                  minDate={values.bookingFrom}
                  popperPlacement="bottom-start"
                />
              </div>
              {errors.bookingTo && touched.bookingTo && <div className="mt-1 text-sm text-red-500">{errors.bookingTo}</div>}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {loading ? t('carBooking.buttons.booking') : t('carBooking.buttons.bookCar')}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default CarBooking
